/**
 * Budget Adjustment Demo Page
 * 
 * PRD-3604: E004-S004 - Adjust Campaign Budgets
 * 
 * Demo page for testing the BudgetAdjustmentSlider component locally
 */

import React, { useState } from 'react';
import { BudgetAdjustmentSlider } from '../components/actions/BudgetAdjustmentSlider';

// Mock campaign data for testing
const mockCampaigns = [
  {
    id: 'camp-001',
    name: 'Summer Sale Campaign - Meta',
    platform: 'meta' as const,
    currentBudget: 100,
    currency: 'USD'
  },
  {
    id: 'camp-002',
    name: 'Brand Awareness Google',
    platform: 'google' as const,
    currentBudget: 500,
    customerId: '123-456-7890',
    budgetId: 'budget-123',
    currency: 'USD'
  },
  {
    id: 'camp-003',
    name: 'TikTok Gen-Z Reach',
    platform: 'tiktok' as const,
    currentBudget: 250,
    advertiserId: 'adv-456',
    currency: 'USD'
  },
  {
    id: 'camp-004',
    name: 'High Budget Campaign',
    platform: 'meta' as const,
    currentBudget: 10000,
    currency: 'USD'
  },
  {
    id: 'camp-005',
    name: 'Minimum Budget Test',
    platform: 'google' as const,
    currentBudget: 15,
    currency: 'USD'
  }
];

export default function BudgetAdjustmentDemo() {
  const [selectedCampaign, setSelectedCampaign] = useState<typeof mockCampaigns[0] | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSuccess = () => {
    setSuccessMessage(`Budget adjusted for ${selectedCampaign?.name}!`);
    setSelectedCampaign(null);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleCancel = () => {
    setSelectedCampaign(null);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Budget Adjustment Demo
          </h1>
          <p className="text-gray-600">
            PRD-3604: Test the BudgetAdjustmentSlider component with different scenarios
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            ✅ {successMessage}
          </div>
        )}

        {/* Campaign Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {mockCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    {campaign.name}
                  </h3>
                  <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded ${
                    campaign.platform === 'meta' 
                      ? 'bg-blue-100 text-blue-800'
                      : campaign.platform === 'google'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {campaign.platform.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500">Current Budget</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${campaign.currentBudget.toLocaleString()}/day
                </p>
              </div>

              <button
                onClick={() => setSelectedCampaign(campaign)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                data-testid="adjust-budget-btn"
              >
                Adjust Budget
              </button>
            </div>
          ))}
        </div>

        {/* Guardrails Reference */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Budget Guardrails Reference
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Minimum Budget</h4>
              <p className="text-2xl font-bold text-red-600">$10</p>
              <p className="text-sm text-gray-500 mt-1">Cannot go below this</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Max Increase</h4>
              <p className="text-2xl font-bold text-orange-600">5x</p>
              <p className="text-sm text-gray-500 mt-1">Maximum multiplier</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Auto-Approve</h4>
              <p className="text-2xl font-bold text-green-600">≤20% & ≤$1K</p>
              <p className="text-sm text-gray-500 mt-1">No approval needed</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Director Approval</h4>
              <p className="text-2xl font-bold text-purple-600">&gt;$10K</p>
              <p className="text-sm text-gray-500 mt-1">Requires director</p>
            </div>
          </div>
        </div>

        {/* Test Scenarios */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Test Scenarios
          </h2>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs">1</span>
              <span><strong>Auto-Approve:</strong> Change $100 → $110 (10% = auto-approve)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs">2</span>
              <span><strong>Manager Approval:</strong> Change $100 → $150 (50% = manager approval)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs">3</span>
              <span><strong>Director Approval:</strong> Change $10,000 → $25,000 ($15K change)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-red-100 text-red-700 rounded-full flex items-center justify-center text-xs">4</span>
              <span><strong>Min Budget Error:</strong> Change any → $5 (below $10 minimum)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-red-100 text-red-700 rounded-full flex items-center justify-center text-xs">5</span>
              <span><strong>Max Ratio Error:</strong> Change $100 → $600 (6x exceeds 5x limit)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Budget Adjustment Modal */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <BudgetAdjustmentSlider
            campaignId={selectedCampaign.id}
            campaignName={selectedCampaign.name}
            platform={selectedCampaign.platform}
            currentBudget={selectedCampaign.currentBudget}
            currency={selectedCampaign.currency}
            customerId={selectedCampaign.platform === 'google' ? (selectedCampaign as any).customerId : undefined}
            budgetId={selectedCampaign.platform === 'google' ? (selectedCampaign as any).budgetId : undefined}
            advertiserId={selectedCampaign.platform === 'tiktok' ? (selectedCampaign as any).advertiserId : undefined}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
          />
        </div>
      )}
    </div>
  );
}
